// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

/**
 * MM/YYYY masking helpers for `date` fields that only need month + year
 * granularity (e.g. "Date employment started"). The value displayed/typed is
 * "MM/YYYY", but the value stored/emitted upward stays a full ISO date string
 * (day defaulted to "01") so it round-trips through the same `type: "date"`
 * schema validation and `FinancialValues.date(...)` parsing every other date
 * field already uses — only the display is month/year.
 */

/**
 * Mask raw — possibly partial, mid-typing — digits into "MM/YYYY". Non-digit
 * characters (e.g. an existing `/`) are stripped first so re-masking is
 * idempotent; input is capped at 6 significant digits (MM + YYYY).
 */
export function maskMonthYear(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 6);
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
}

/**
 * Parse a complete "MM/YYYY" string into an ISO date (`YYYY-MM-01`), or
 * `undefined` when incomplete, blank, or the month isn't 01–12.
 */
export function parseMonthYear(text: string): string | undefined {
  const match = text.match(/^(\d{2})\/(\d{4})$/);
  if (!match) return undefined;
  const month = Number(match[1]);
  if (month < 1 || month > 12) return undefined;
  return `${match[2]}-${match[1]}-01`;
}

/**
 * Format a stored ISO date value (`YYYY-MM-DD`) for display as "MM/YYYY".
 * Non-ISO-date values (including non-strings) → ''.
 */
export function formatMonthYear(value: unknown): string {
  if (typeof value !== 'string') return '';
  const match = value.match(/^(\d{4})-(\d{2})-\d{2}$/);
  return match ? `${match[2]}/${match[1]}` : '';
}
